import {
  FC,
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { ViewScheduleLineItemsDialogProps } from './ViewScheduleLineItemsDialog.types';
import {
  Dialog,
  Flex,
  Column,
  Input,
  Button,
  EmptyState,
  Select,
} from '@/_components';
import {
  getPriorYearScheduleLineItems,
  getPriorYearScheduleLineItemsByPriorYearId,
  addPriorYearScheduleLineItem,
  addPriorYearScheduleLineItemWithoutCompany,
} from '@/_api/admin-prior-year-items';
import { ScheduleLineItem } from './_components/ScheduleLineItem';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { getSelectableScheduleLineItems } from '@/_utils/questionnaire';
import { uniqBy } from '@/_utils/array';

export const ViewScheduleLineItemsDialog: FC<
  ViewScheduleLineItemsDialogProps
> = ({ isOpen, onClose, companyId, priorYearItemId, priorYearItemName }) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [lineItems, setLineItems] = useState<any[]>([]);

  const getLineItems = useCallback(async () => {
    let response;

    if (companyId) {
      response = await getPriorYearScheduleLineItems({
        companyId,
        priorYearItemId,
        token,
      });
    } else {
      response = await getPriorYearScheduleLineItemsByPriorYearId({
        priorYearItemId,
        token,
      });
    }

    setLineItems(response as any[]);
  }, [companyId, priorYearItemId, token]);

  useEffect(() => {
    if (isOpen) {
      getLineItems();
    }
  }, [isOpen, getLineItems]);

  const [addingNew, setAddingNew] = useState(false);
  const [addingCustom, setAddingCustom] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [customCategoryInner, setCustomCategoryInner] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);

  const saveNew = useCallback(async () => {
    if (!newCategory) {
      addToast('error', 'Please specify a line item title');
      return;
    }

    if (!newValue) {
      addToast('error', 'Please specify a line item value');
      return;
    }

    try {
      setSaving(true);
      if (companyId) {
        await addPriorYearScheduleLineItem({
          companyId,
          priorYearItemId,
          category: newCategory,
          value: newValue,
          token,
        });
      } else {
        await addPriorYearScheduleLineItemWithoutCompany({
          priorYearItemId,
          category: newCategory,
          value: newValue,
          token,
        });
      }
    } catch (error) {
      addToast('error', 'Failed to save line item', { error });
    } finally {
      await getLineItems();
      setAddingNew(false);
      setAddingCustom(false);
      setNewValue('');
      setNewCategory('');
      setCustomCategoryInner('');
      setSaving(false);
    }
  }, [
    companyId,
    token,
    priorYearItemId,
    newCategory,
    newValue,
    getLineItems,
    addToast,
  ]);

  const cancel = useCallback(() => {
    setAddingNew(false);
    setAddingCustom(false);
    setNewValue('');
    setNewCategory('');
    setCustomCategoryInner('');
  }, []);

  const handleCustomCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const innerValue = e.target.value;
      setCustomCategoryInner(innerValue);
      setNewCategory(`Other (${innerValue})`);
    },
    [],
  );

  const selectableCategories = useMemo(() => {
    const options = getSelectableScheduleLineItems(priorYearItemName).concat(
      lineItems.map((lineItem) => {
        return {
          label: lineItem.category,
          value: lineItem.category,
        };
      }),
    );

    return uniqBy(options, 'value');
  }, [priorYearItemName, lineItems]);

  return (
    <Dialog
      title={'Line Items for ' + priorYearItemName}
      onClose={onClose}
      isOpen={isOpen}
    >
      <Dialog.Content>
        {lineItems.length > 0 ? (
          <Flex isColumn gap={8}>
            {lineItems.map((lineItem) => (
              <ScheduleLineItem
                key={lineItem.id}
                companyId={companyId}
                lineItem={lineItem}
                selectableCategories={selectableCategories}
                reloadScheduleLineItems={getLineItems}
              />
            ))}
          </Flex>
        ) : (
          <EmptyState description="No line items found" icon="receipt" />
        )}
      </Dialog.Content>

      <Dialog.Footer>
        {!addingNew && !addingCustom ? (
          <Flex isFullWidth xAlign="end" gap={4}>
            <Button
              value="Add custom"
              iconLeft="plus"
              intent="secondary"
              size="small"
              onClick={() => setAddingCustom(true)}
            />
            <Button
              value="Add new"
              iconLeft="plus"
              intent="secondary"
              size="small"
              onClick={() => setAddingNew(true)}
            />
          </Flex>
        ) : (
          <Flex gap={4} isYCentered>
            <Column width={420}>
              {addingNew ? (
                <Select
                  label="Category"
                  isLabelHidden={true}
                  items={selectableCategories?.map((category) => ({
                    name: category.label,
                    value: category.value,
                  }))}
                  selectedValue={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  isFullWidth
                />
              ) : (
                <Input
                  label="Category"
                  isLabelHidden={true}
                  value={customCategoryInner}
                  onChange={handleCustomCategoryChange}
                  placeholder="Enter category name"
                />
              )}
            </Column>

            <Input
              label={
                addingCustom && customCategoryInner
                  ? `Other (${customCategoryInner})`
                  : newCategory
              }
              isLabelHidden={true}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />

            <Button
              value="Save"
              isIconOnly
              intent="secondary"
              size="small"
              withTooltip={true}
              iconLeft="check"
              onClick={saveNew}
              isLoading={saving}
            />
            <Button
              value="Cancel"
              isIconOnly
              intent="danger-secondary"
              size="small"
              withTooltip={true}
              iconLeft="x"
              onClick={cancel}
              isDisabled={saving}
            />
          </Flex>
        )}
      </Dialog.Footer>
    </Dialog>
  );
};
